import { z } from 'zod';
import { CreateMediaUploadResponseSchema } from '../media/files.schemas';
import { refersTo } from '../common/refers-to';

/**
 * Property-document category taxonomy (IMM-218). Category tokens are en-US
 * (code register); their German display labels live in `@immo/i18n`. The group
 * is always DERIVED from the category via {@link CATEGORY_GROUP} — never chosen
 * separately, never accepted from the client. `UNCATEGORIZED` is the default
 * landing state, grouped under `OTHER` but surfaced as its own list filter.
 */
export const DocumentCategoryGroupSchema = z.enum([
  'MARKETING',
  'ENERGY_TECHNICAL',
  'OWNERSHIP_LAND_REGISTRY',
  'BUILDING_PERMITS',
  'ADMINISTRATION',
  'CONTRACTS_TRANSACTION',
  'FINANCE_TAX',
  'PERSON_LEGITIMATION',
  'INSURANCE',
  'OTHER',
]);

export type DocumentCategoryGroup = z.infer<typeof DocumentCategoryGroupSchema>;

export const DocumentCategorySchema = z.enum([
  // Vermarktung
  'EXPOSE',
  'FLOOR_PLAN',
  'SITE_PLAN',
  'LIVING_AREA_CALCULATION',
  // Energie & Technik
  'ENERGY_CERTIFICATE',
  'MAINTENANCE_RECORD',
  'TECHNICAL_REPORT',
  // Eigentum & Grundbuch
  'LAND_REGISTER_EXTRACT',
  'CADASTRAL_MAP',
  'BUILDING_ENCUMBRANCE',
  'CONTAMINATED_SITES_REGISTER_EXTRACT',
  'DECLARATION_OF_DIVISION',
  'PARTITION_PLAN',
  'CERTIFICATE_OF_COMPLETENESS',
  'HERITABLE_BUILDING_RIGHT',
  // Bau & Genehmigung
  'BUILDING_PERMIT',
  'BUILDING_DESCRIPTION',
  'BUILDING_FILE',
  'HERITAGE_PROTECTION',
  'RENOVATION_RECORD',
  // Verwaltung (WEG & Miete)
  'OWNERS_MEETING_MINUTES',
  'RESOLUTION_COLLECTION',
  'ECONOMIC_PLAN',
  'HOA_STATEMENT',
  'PROPERTY_MANAGEMENT_CONTRACT',
  'RENTAL_CONTRACT',
  'UTILITY_STATEMENT',
  'TENANT_LIST',
  'RESIDENCE_CONFIRMATION',
  'HOUSE_RULES',
  // Verträge & Transaktion
  'BROKER_CONTRACT',
  'RESERVATION_AGREEMENT',
  'PURCHASE_CONTRACT',
  'HANDOVER_PROTOCOL',
  'POWER_OF_ATTORNEY',
  // Finanzen & Steuern
  'PROPERTY_TAX_ASSESSMENT',
  'VALUATION_REPORT',
  'ACQUISITION_BRIEFING',
  'VALUATION',
  'FINANCING_CONFIRMATION',
  // Person & Legitimation
  'SELF_DISCLOSURE',
  'CREDIT_REPORT',
  'IDENTITY_DOCUMENT',
  'INCOME_PROOF',
  // Versicherung
  'INSURANCE_CERTIFICATE',
  // Sonstiges
  'MISC',
  'UNCATEGORIZED',
]);

export type DocumentCategory = z.infer<typeof DocumentCategorySchema>;

/** Categories only the system files (IMM-1391): never member-assignable, never classified, never analysed. */
export const SYSTEM_FILED_DOCUMENT_CATEGORIES = [
  'ACQUISITION_BRIEFING',
  'VALUATION',
] as const satisfies ReadonlyArray<DocumentCategory>;

/** What a member or a member's client may assign. Every request body and tool input that takes a category uses it. */
export const AssignableDocumentCategorySchema = DocumentCategorySchema.exclude([...SYSTEM_FILED_DOCUMENT_CATEGORIES]);

export type AssignableDocumentCategory = z.infer<typeof AssignableDocumentCategorySchema>;

export function isAssignableDocumentCategory(category: DocumentCategory): category is AssignableDocumentCategory {
  return !(SYSTEM_FILED_DOCUMENT_CATEGORIES as ReadonlyArray<DocumentCategory>).includes(category);
}

/** IMM-1391 — valuation documents: never „Für Käufer“, so never in a Datenraum. */
export const VALUATION_DOCUMENT_CATEGORIES = [
  'VALUATION_REPORT',
  'ACQUISITION_BRIEFING',
  'VALUATION',
] as const satisfies ReadonlyArray<DocumentCategory>;

export function isValuationDocumentCategory(category: DocumentCategory): boolean {
  return (VALUATION_DOCUMENT_CATEGORIES as ReadonlyArray<DocumentCategory>).includes(category);
}

export const DEFAULT_DOCUMENT_CATEGORY: DocumentCategory = 'UNCATEGORIZED';

/**
 * How a document's {@link DocumentCategorySchema} was decided (IMM-451.1):
 * `AUTO` — the async classifier placed it confidently; `MANUAL` — the Makler
 * picked it (at confirm or via re-categorise); `NONE` — no confident category
 * yet (the default landing state, and the below-threshold / photo-held fallback).
 * Never client-set; derived server-side.
 */
export const DocumentCategorySourceSchema = z.enum(['AUTO', 'MANUAL', 'NONE']);

export type DocumentCategorySource = z.infer<typeof DocumentCategorySourceSchema>;

export const DEFAULT_DOCUMENT_CATEGORY_SOURCE: DocumentCategorySource = 'NONE';

/**
 * The async auto-classification lifecycle of a document (IMM-451.1). `PENDING`
 * — queued, not yet claimed; `CLASSIFYING` — claimed by the poller (the atomic
 * in-progress marker, mirrors analysis `ANALYZING`); `DONE` — classified (a
 * confident category was applied, or it was held `UNCATEGORIZED` below-threshold
 * / as a photo); `FAILED` — the classifier errored or the bytes were gone (stays
 * `UNCATEGORIZED`, terminal in V1). The UI collapses `PENDING` + `CLASSIFYING`
 * into one „Wird einsortiert …" state — the PRD's 3-state list is that
 * UI-facing simplification.
 */
export const DocumentClassificationStatusSchema = z.enum(['PENDING', 'CLASSIFYING', 'DONE', 'FAILED']);

export type DocumentClassificationStatus = z.infer<typeof DocumentClassificationStatusSchema>;

export const DEFAULT_DOCUMENT_CLASSIFICATION_STATUS: DocumentClassificationStatus = 'PENDING';

/**
 * Whether a document's classification is still in flight, and therefore whether
 * the list showing it must keep refreshing. `null` is **not** in flight: it means
 * the row has no Objekt classification at all — a valuation attachment written
 * before IMM-501.1, an image, or a reference that no longer resolves. Reading
 * null as "not yet" would make every legacy request refresh forever.
 *
 * It lives beside the enum rather than in either SPA because both the agent's
 * Unterlagen list and the operator's request detail (IMM-501.3) decide the same
 * thing from it, and a restated status list is how the two would drift.
 */
export function isClassificationInFlight(status: DocumentClassificationStatus | null): boolean {
  return status === 'PENDING' || status === 'CLASSIFYING';
}

/**
 * How often a list re-reads while a document is still being auto-classified
 * (IMM-451.1). Frequent enough that a just-uploaded batch settles visibly within
 * seconds, matching the backend classification poller cadence.
 */
export const CLASSIFICATION_POLL_MS = 4_000;

/** Maps every category to its group. The group drives list grouping/filtering. */
export const CATEGORY_GROUP = {
  EXPOSE: 'MARKETING',
  FLOOR_PLAN: 'MARKETING',
  SITE_PLAN: 'MARKETING',
  LIVING_AREA_CALCULATION: 'MARKETING',
  ENERGY_CERTIFICATE: 'ENERGY_TECHNICAL',
  MAINTENANCE_RECORD: 'ENERGY_TECHNICAL',
  TECHNICAL_REPORT: 'ENERGY_TECHNICAL',
  LAND_REGISTER_EXTRACT: 'OWNERSHIP_LAND_REGISTRY',
  CADASTRAL_MAP: 'OWNERSHIP_LAND_REGISTRY',
  BUILDING_ENCUMBRANCE: 'OWNERSHIP_LAND_REGISTRY',
  CONTAMINATED_SITES_REGISTER_EXTRACT: 'OWNERSHIP_LAND_REGISTRY',
  DECLARATION_OF_DIVISION: 'OWNERSHIP_LAND_REGISTRY',
  PARTITION_PLAN: 'OWNERSHIP_LAND_REGISTRY',
  CERTIFICATE_OF_COMPLETENESS: 'OWNERSHIP_LAND_REGISTRY',
  HERITABLE_BUILDING_RIGHT: 'OWNERSHIP_LAND_REGISTRY',
  BUILDING_PERMIT: 'BUILDING_PERMITS',
  BUILDING_DESCRIPTION: 'BUILDING_PERMITS',
  BUILDING_FILE: 'BUILDING_PERMITS',
  HERITAGE_PROTECTION: 'BUILDING_PERMITS',
  RENOVATION_RECORD: 'BUILDING_PERMITS',
  OWNERS_MEETING_MINUTES: 'ADMINISTRATION',
  RESOLUTION_COLLECTION: 'ADMINISTRATION',
  ECONOMIC_PLAN: 'ADMINISTRATION',
  HOA_STATEMENT: 'ADMINISTRATION',
  PROPERTY_MANAGEMENT_CONTRACT: 'ADMINISTRATION',
  RENTAL_CONTRACT: 'ADMINISTRATION',
  UTILITY_STATEMENT: 'ADMINISTRATION',
  TENANT_LIST: 'ADMINISTRATION',
  RESIDENCE_CONFIRMATION: 'ADMINISTRATION',
  HOUSE_RULES: 'ADMINISTRATION',
  BROKER_CONTRACT: 'CONTRACTS_TRANSACTION',
  RESERVATION_AGREEMENT: 'CONTRACTS_TRANSACTION',
  PURCHASE_CONTRACT: 'CONTRACTS_TRANSACTION',
  HANDOVER_PROTOCOL: 'CONTRACTS_TRANSACTION',
  POWER_OF_ATTORNEY: 'CONTRACTS_TRANSACTION',
  PROPERTY_TAX_ASSESSMENT: 'FINANCE_TAX',
  VALUATION_REPORT: 'FINANCE_TAX',
  ACQUISITION_BRIEFING: 'FINANCE_TAX',
  VALUATION: 'FINANCE_TAX',
  FINANCING_CONFIRMATION: 'FINANCE_TAX',
  SELF_DISCLOSURE: 'PERSON_LEGITIMATION',
  CREDIT_REPORT: 'PERSON_LEGITIMATION',
  IDENTITY_DOCUMENT: 'PERSON_LEGITIMATION',
  INCOME_PROOF: 'PERSON_LEGITIMATION',
  INSURANCE_CERTIFICATE: 'INSURANCE',
  MISC: 'OTHER',
  UNCATEGORIZED: 'OTHER',
} as const satisfies Record<DocumentCategory, DocumentCategoryGroup>;

export function deriveCategoryGroup(category: DocumentCategory): DocumentCategoryGroup {
  return CATEGORY_GROUP[category];
}

/**
 * The two top-level sections of the Dokumente tab (IMM-1215): what markets the
 * Objekt, and the agency's own file on it. Like the group, the section is always
 * DERIVED from the category via {@link CATEGORY_SECTION}: never stored, never
 * on the wire, never chosen. Order is display order.
 */
export const DOCUMENT_SECTION_ORDER = ['PROPERTY', 'INTERNAL'] as const;

export type DocumentSection = (typeof DOCUMENT_SECTION_ORDER)[number];

/** Maps every category to its section. `PROPERTY` unless it is the agency's own paperwork. */
export const CATEGORY_SECTION = {
  EXPOSE: 'PROPERTY',
  FLOOR_PLAN: 'PROPERTY',
  SITE_PLAN: 'PROPERTY',
  LIVING_AREA_CALCULATION: 'PROPERTY',
  ENERGY_CERTIFICATE: 'PROPERTY',
  MAINTENANCE_RECORD: 'PROPERTY',
  TECHNICAL_REPORT: 'PROPERTY',
  LAND_REGISTER_EXTRACT: 'PROPERTY',
  CADASTRAL_MAP: 'PROPERTY',
  BUILDING_ENCUMBRANCE: 'PROPERTY',
  CONTAMINATED_SITES_REGISTER_EXTRACT: 'PROPERTY',
  DECLARATION_OF_DIVISION: 'PROPERTY',
  PARTITION_PLAN: 'PROPERTY',
  CERTIFICATE_OF_COMPLETENESS: 'PROPERTY',
  HERITABLE_BUILDING_RIGHT: 'PROPERTY',
  BUILDING_PERMIT: 'PROPERTY',
  BUILDING_DESCRIPTION: 'PROPERTY',
  BUILDING_FILE: 'PROPERTY',
  HERITAGE_PROTECTION: 'PROPERTY',
  RENOVATION_RECORD: 'PROPERTY',
  OWNERS_MEETING_MINUTES: 'PROPERTY',
  RESOLUTION_COLLECTION: 'PROPERTY',
  ECONOMIC_PLAN: 'PROPERTY',
  HOA_STATEMENT: 'PROPERTY',
  PROPERTY_MANAGEMENT_CONTRACT: 'PROPERTY',
  RENTAL_CONTRACT: 'PROPERTY',
  UTILITY_STATEMENT: 'PROPERTY',
  TENANT_LIST: 'PROPERTY',
  RESIDENCE_CONFIRMATION: 'PROPERTY',
  HOUSE_RULES: 'PROPERTY',
  BROKER_CONTRACT: 'INTERNAL',
  RESERVATION_AGREEMENT: 'INTERNAL',
  PURCHASE_CONTRACT: 'INTERNAL',
  HANDOVER_PROTOCOL: 'INTERNAL',
  POWER_OF_ATTORNEY: 'INTERNAL',
  PROPERTY_TAX_ASSESSMENT: 'PROPERTY',
  VALUATION_REPORT: 'INTERNAL',
  ACQUISITION_BRIEFING: 'INTERNAL',
  VALUATION: 'INTERNAL',
  FINANCING_CONFIRMATION: 'INTERNAL',
  SELF_DISCLOSURE: 'INTERNAL',
  CREDIT_REPORT: 'INTERNAL',
  IDENTITY_DOCUMENT: 'INTERNAL',
  INCOME_PROOF: 'INTERNAL',
  INSURANCE_CERTIFICATE: 'PROPERTY',
  MISC: 'PROPERTY',
  UNCATEGORIZED: 'PROPERTY',
} as const satisfies Record<DocumentCategory, DocumentSection>;

export function deriveCategorySection(category: DocumentCategory): DocumentSection {
  return CATEGORY_SECTION[category];
}

/** Group display order for the filter bar and the grouped list (PRD §Copy order). */
export const DOCUMENT_GROUP_ORDER = [
  'MARKETING',
  'ENERGY_TECHNICAL',
  'OWNERSHIP_LAND_REGISTRY',
  'BUILDING_PERMITS',
  'ADMINISTRATION',
  'CONTRACTS_TRANSACTION',
  'FINANCE_TAX',
  'PERSON_LEGITIMATION',
  'INSURANCE',
  'OTHER',
] as const satisfies ReadonlyArray<DocumentCategoryGroup>;

/** Categories per group, in taxonomy order — drives the grouped category picker. */
export const DOCUMENT_CATEGORIES_BY_GROUP: Record<DocumentCategoryGroup, ReadonlyArray<DocumentCategory>> = {
  MARKETING: ['EXPOSE', 'FLOOR_PLAN', 'SITE_PLAN', 'LIVING_AREA_CALCULATION'],
  ENERGY_TECHNICAL: ['ENERGY_CERTIFICATE', 'MAINTENANCE_RECORD', 'TECHNICAL_REPORT'],
  OWNERSHIP_LAND_REGISTRY: [
    'LAND_REGISTER_EXTRACT',
    'CADASTRAL_MAP',
    'BUILDING_ENCUMBRANCE',
    'CONTAMINATED_SITES_REGISTER_EXTRACT',
    'DECLARATION_OF_DIVISION',
    'PARTITION_PLAN',
    'CERTIFICATE_OF_COMPLETENESS',
    'HERITABLE_BUILDING_RIGHT',
  ],
  BUILDING_PERMITS: [
    'BUILDING_PERMIT',
    'BUILDING_DESCRIPTION',
    'BUILDING_FILE',
    'HERITAGE_PROTECTION',
    'RENOVATION_RECORD',
  ],
  ADMINISTRATION: [
    'OWNERS_MEETING_MINUTES',
    'RESOLUTION_COLLECTION',
    'ECONOMIC_PLAN',
    'HOA_STATEMENT',
    'PROPERTY_MANAGEMENT_CONTRACT',
    'RENTAL_CONTRACT',
    'UTILITY_STATEMENT',
    'TENANT_LIST',
    'RESIDENCE_CONFIRMATION',
    'HOUSE_RULES',
  ],
  CONTRACTS_TRANSACTION: [
    'BROKER_CONTRACT',
    'RESERVATION_AGREEMENT',
    'PURCHASE_CONTRACT',
    'HANDOVER_PROTOCOL',
    'POWER_OF_ATTORNEY',
  ],
  FINANCE_TAX: ['PROPERTY_TAX_ASSESSMENT', 'VALUATION_REPORT', 'FINANCING_CONFIRMATION'],
  PERSON_LEGITIMATION: ['SELF_DISCLOSURE', 'CREDIT_REPORT', 'IDENTITY_DOCUMENT', 'INCOME_PROOF'],
  INSURANCE: ['INSURANCE_CERTIFICATE'],
  // `UNCATEGORIZED` is a default state surfaced via its own filter, not offered in the picker.
  OTHER: ['MISC'],
};

/**
 * The analysable document-type universe, flattened from the grouped picker set in
 * group + taxonomy order. Excludes `UNCATEGORIZED` (a default state, never in the
 * picker); „OTHER" is `MISC` only. This is the key space for the admin
 * managed-prompt Skills surface (IMM-406.2) — one skill per category — and the
 * order the grouped list renders in.
 */
export const ANALYSABLE_DOCUMENT_CATEGORIES: ReadonlyArray<DocumentCategory> = DOCUMENT_GROUP_ORDER.flatMap(
  (group) => DOCUMENT_CATEGORIES_BY_GROUP[group],
);

/**
 * V1 upload policy (server-authoritative). PDF, DOCX, JPEG, PNG; ≤ 20 MB. The
 * consumer (property module) passes these to the media port — the media module
 * holds no policy of its own.
 */
export const DOCUMENT_ALLOWED_CONTENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
] as const;

export const DOCUMENT_MAX_SIZE_BYTES = 20 * 1024 * 1024;

/**
 * The image-only upload policy, for attachment sets that accept photos and
 * nothing else (the valuation request's image block, IMM-447.2). Server-
 * authoritative in the same way as the document policy above — the consumer
 * chooses which of the two applies, the client never supplies either.
 */
export const IMAGE_ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png'] as const;

export const IMAGE_MAX_SIZE_BYTES = 25 * 1024 * 1024;

export const DOCUMENT_MEDIA_DESCRIPTION = 'property-document';

export const PropertyDocumentSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  displayName: z.string(),
  originalFilename: z.string(),
  category: DocumentCategorySchema,
  categoryGroup: DocumentCategoryGroupSchema,
  // How `category` was decided, and where the async auto-classification stands
  // (IMM-451.1). Both are server-derived, never client-set; the list polls while
  // any row is `PENDING`/`CLASSIFYING` and renders the „Wird einsortiert …" /
  // „Automatisch einsortiert" markers from them.
  categorySource: DocumentCategorySourceSchema,
  classificationStatus: DocumentClassificationStatusSchema,
  // Set only on a redacted copy; null on an original and on a copy whose original was deleted (IMM-249).
  redacted: z.boolean(),
  redactedFromDocumentId: z.uuid().nullable(),
  // IMM-1176.1 — marked „Für Käufer“: listed for every Kaufinteressent with an active Datenraum release. Never true for a Person & Legitimation category (DB CHECK).
  forBuyers: z.boolean(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  // Null when a MACHINE filed the document (IMM-587.4): an admin API key is bound
  // to an operator and has no member, so there is no member id to report. The
  // operator is recorded on the row for the audit trail but is deliberately not
  // exposed here — `uploadedByName` answers "immoOS" for those.
  uploadedByMemberId: refersTo('member', z.uuid()).nullable(),
  // Null when the uploader is no longer an active workspace member; the frontend
  // renders the "unknown member" placeholder via i18n (no German in the backend).
  // "immoOS" for a machine-filed document.
  uploadedByName: z.string().nullable(),
  // IMM-1391 — true on a PDF the valuation agent filed; its `uploadedByName` is null and a client shows its own label.
  uploadedByValuationAgent: z.boolean(),
  uploadedAt: z.iso.datetime(),
  // IMM-1391 — set once a newer result of the same kind replaced this one („Ersetzt am“).
  supersededAt: z.iso.datetime().nullable(),
});

export type PropertyDocument = z.infer<typeof PropertyDocumentSchema>;

export const PropertyDocumentBuyerViewsSchema = z.object({
  viewedBy: z.number().int().nonnegative(),
  activeReleases: z.number().int().nonnegative(),
});

export type PropertyDocumentBuyerViews = z.infer<typeof PropertyDocumentBuyerViewsSchema>;

/**
 * A document as it appears in the list, carrying the base `PropertyDocument`
 * plus the per-row `canManage` flag (IMM-218.3): `true` when the caller is the
 * uploader OR holds `document-manage-any` (Owner / Admin). The frontend uses it
 * to show the manage kebab (Umbenennen · Bearbeiten · Löschen) vs the read-only
 * kebab — the write endpoints still re-check server-side, this only drives the
 * UI hide-not-disable layer.
 */
export const PROPERTY_DOCUMENT_DELETION_LOCKS = ['SIGNED_DOCUMENT'] as const;
export type PropertyDocumentDeletionLock = (typeof PROPERTY_DOCUMENT_DELETION_LOCKS)[number];

export const PropertyDocumentListItemSchema = PropertyDocumentSchema.extend({
  canManage: z.boolean(),
  deletionLock: z.enum(PROPERTY_DOCUMENT_DELETION_LOCKS).nullable(), // a filed, fully signed contract (IMM-1171.5)
  // Null unless forBuyers: the counter belongs to the marked set only (IMM-1176.5).
  buyerViews: PropertyDocumentBuyerViewsSchema.nullable(),
});

export type PropertyDocumentListItem = z.infer<typeof PropertyDocumentListItemSchema>;

export const PropertyDocumentsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

/** Path params addressing one document under a property (rename / re-categorise / delete). */
export const PropertyDocumentPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  documentId: z.uuid(),
});

export const CreatePropertyDocumentUploadBodySchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255),
  })
  .strict();

export type CreatePropertyDocumentUploadBody = z.infer<typeof CreatePropertyDocumentUploadBodySchema>;

// The presign response is the media module's `CreateMediaUploadResponse` verbatim
// (S3 POST target + pending file id) — documents add no fields of their own here.
export const CreatePropertyDocumentUploadResponseSchema = CreateMediaUploadResponseSchema;

export type CreatePropertyDocumentUploadResponse = z.infer<typeof CreatePropertyDocumentUploadResponseSchema>;

export const ConfirmPropertyDocumentUploadBodySchema = z
  .object({
    fileId: z.uuid(),
    category: AssignableDocumentCategorySchema.optional(),
    displayName: z.string().trim().min(1).max(255).optional(),
  })
  .strict();

export type ConfirmPropertyDocumentUploadBody = z.infer<typeof ConfirmPropertyDocumentUploadBodySchema>;

export const ConfirmPropertyDocumentUploadResponseSchema = PropertyDocumentSchema;

export type ConfirmPropertyDocumentUploadResponse = z.infer<typeof ConfirmPropertyDocumentUploadResponseSchema>;

export const ListPropertyDocumentsQuerySchema = z
  .object({
    // A category group, or the sentinel 'UNCATEGORIZED' for documents awaiting a category.
    group: z.union([DocumentCategoryGroupSchema, z.literal('UNCATEGORIZED')]).optional(),
  })
  .strict();

export type ListPropertyDocumentsQuery = z.infer<typeof ListPropertyDocumentsQuerySchema>;

export const ListPropertyDocumentsResponseSchema = z.object({
  data: z.array(PropertyDocumentListItemSchema),
  uncategorizedCount: z.number().int().nonnegative(),
  // The documents a bulk analysis run would queue (IMM-550.1) — settled
  // classification, and no usable analysis result. Like `uncategorizedCount`
  // this is the WHOLE property's number, independent of the `group` filter: a
  // member narrowed to one group must not see the banner shrink to it.
  analysisBacklogCount: z.number().int().nonnegative(),
  // System-derived: whether a redaction engine is configured, so the action may be offered (IMM-249).
  redactionAvailable: z.boolean(),
});

export type ListPropertyDocumentsResponse = z.infer<typeof ListPropertyDocumentsResponseSchema>;

// Read mode → media content-disposition: `preview` mints an inline URL (the
// browser renders it), `download` mints an attachment URL (the browser saves it
// under the original filename). Default is `download` (IMM-218.2).
export const DOCUMENT_READ_MODES = ['preview', 'download'] as const;

export type DocumentReadMode = (typeof DOCUMENT_READ_MODES)[number];

export const DEFAULT_DOCUMENT_READ_MODE: DocumentReadMode = 'download';

export const ReadPropertyDocumentPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  documentId: z.uuid(),
});

export const ReadPropertyDocumentQuerySchema = z
  .object({
    mode: z.enum(DOCUMENT_READ_MODES).optional(),
  })
  .strict();

export type ReadPropertyDocumentQuery = z.infer<typeof ReadPropertyDocumentQuerySchema>;

export const ReadPropertyDocumentResponseSchema = z.object({
  url: z.url(),
  expiresInSeconds: z.number().int().positive(),
});

export type ReadPropertyDocumentResponse = z.infer<typeof ReadPropertyDocumentResponseSchema>;

// Rename: only the display name changes (1–255 chars). Original filename,
// uploader and upload date are immutable stamps (IMM-218.3).
export const RenamePropertyDocumentBodySchema = z
  .object({
    displayName: z.string().trim().min(1).max(255),
  })
  .strict();

export type RenamePropertyDocumentBody = z.infer<typeof RenamePropertyDocumentBodySchema>;

export const RenamePropertyDocumentResponseSchema = PropertyDocumentSchema;

export type RenamePropertyDocumentResponse = z.infer<typeof RenamePropertyDocumentResponseSchema>;

// Re-categorise: only the category changes; the group is re-derived server-side.
export const RecategorizePropertyDocumentBodySchema = z
  .object({
    category: AssignableDocumentCategorySchema,
  })
  .strict();

export type RecategorizePropertyDocumentBody = z.infer<typeof RecategorizePropertyDocumentBodySchema>;

export const RecategorizePropertyDocumentResponseSchema = PropertyDocumentSchema;

export type RecategorizePropertyDocumentResponse = z.infer<typeof RecategorizePropertyDocumentResponseSchema>;

// IMM-1176.1 — set or clear the „Für Käufer“ mark. Idempotent: the current value changes nothing.
export const SetPropertyDocumentForBuyersBodySchema = z.object({ forBuyers: z.boolean() }).strict();

export type SetPropertyDocumentForBuyersBody = z.infer<typeof SetPropertyDocumentForBuyersBodySchema>;

export const SetPropertyDocumentForBuyersResponseSchema = PropertyDocumentSchema;

export type SetPropertyDocumentForBuyersResponse = z.infer<typeof SetPropertyDocumentForBuyersResponseSchema>;

// IMM-1329.1 — why one document of a batch was not changed.
export const PROPERTY_DOCUMENT_FOR_BUYERS_FAILURES = [
  'NOT_FOUND',
  'PERSONAL_DOCUMENT',
  'VALUATION_DOCUMENT',
  'FORBIDDEN',
] as const;
export const PropertyDocumentForBuyersFailureSchema = z.enum(PROPERTY_DOCUMENT_FOR_BUYERS_FAILURES);
export type PropertyDocumentForBuyersFailure = z.infer<typeof PropertyDocumentForBuyersFailureSchema>;

export const SetPropertyDocumentsForBuyersBodySchema = z
  .object({
    documentIds: z
      .array(z.uuid())
      .min(1)
      .max(500)
      .refine((ids) => new Set(ids).size === ids.length, { message: 'documentIds must be unique' }),
    forBuyers: z.boolean(),
  })
  .strict();

export type SetPropertyDocumentsForBuyersBody = z.infer<typeof SetPropertyDocumentsForBuyersBodySchema>;

export const PropertyDocumentForBuyersResultSchema = z.discriminatedUnion('outcome', [
  z.object({ documentId: z.uuid(), outcome: z.literal('CHANGED'), reason: z.null() }),
  z.object({ documentId: z.uuid(), outcome: z.literal('UNCHANGED'), reason: z.null() }),
  z.object({ documentId: z.uuid(), outcome: z.literal('FAILED'), reason: PropertyDocumentForBuyersFailureSchema }),
]);

export type PropertyDocumentForBuyersResult = z.infer<typeof PropertyDocumentForBuyersResultSchema>;

// One row per requested id, in request order.
export const SetPropertyDocumentsForBuyersResponseSchema = z.object({
  results: z.array(PropertyDocumentForBuyersResultSchema),
});

export type SetPropertyDocumentsForBuyersResponse = z.infer<typeof SetPropertyDocumentsForBuyersResponseSchema>;

// Delete: hard-deletes the metadata row and its bytes; returns the removed id.
export const DeletePropertyDocumentResponseSchema = z.object({
  id: z.uuid(),
});

export type DeletePropertyDocumentResponse = z.infer<typeof DeletePropertyDocumentResponseSchema>;

// Re-enqueue the property's uncategorised documents for auto-classification
// (IMM-483.1). `queued` counts the rows this run moved back to `PENDING`;
// `uncategorized` is the property's total `UNCATEGORIZED` count after the run.
// The two differ when documents are excluded (a MANUAL clearing) or already in
// flight, which lets the UI tell „nothing to do" (0/0) from „already running"
// (0 queued, N uncategorised).
export const RequeueDocumentClassificationResponseSchema = z.object({
  queued: z.number().int().nonnegative(),
  uncategorized: z.number().int().nonnegative(),
});

export type RequeueDocumentClassificationResponse = z.infer<typeof RequeueDocumentClassificationResponseSchema>;

// Queue an analysis for every one of the property's documents without a usable
// result (IMM-550.1). `queued` counts the `PENDING` analyses this run inserted;
// `inFlight` is how many of the property's documents were already `PENDING` /
// `ANALYZING` and so were deliberately not re-queued. `queued: 0` alone is
// ambiguous — „nothing to do" and „a run is already going" need different
// messages, and `inFlight` is what tells them apart.
export const QueueDocumentAnalysisResponseSchema = z.object({
  queued: z.number().int().nonnegative(),
  inFlight: z.number().int().nonnegative(),
});

export type QueueDocumentAnalysisResponse = z.infer<typeof QueueDocumentAnalysisResponseSchema>;
